/**
 * Utility functions for Deep Research progress tracking.
 * Provides helper functions for tool filtering and display names.
 * The actual tool call config is defined in useMessageThread.ts.
 */

import {ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {
  DeepResearchToolType,
  TOOL_DESCRIPTIONS_COMPLETED,
  TOOL_DESCRIPTIONS_IN_PROGRESS,
  isMCPTool,
} from 'llm/deep-research/tool-details/types';

/**
 * Tools to include in progress display for Deep Research.
 */
const TOOLS_TO_DISPLAY = [
  DeepResearchToolType.SearchTool,
  DeepResearchToolType.SearchNotebookEntries,
  DeepResearchToolType.SearchSequencesTool,
  DeepResearchToolType.GetWarehouseOverviewTool,
  DeepResearchToolType.QueryWarehouseTool,
  DeepResearchToolType.ReadBenchlingDocumentTool,
  DeepResearchToolType.AttachmentTool,
  DeepResearchToolType.PublicDataSubAgent,
  DeepResearchToolType.IdsToModelsTool,
  DeepResearchToolType.PlottingTool,
  DeepResearchToolType.GenerateImageTool,
  DeepResearchToolType.Think,
];

/**
 * Check if a tool should be displayed in the Deep Research progress UI.
 */
export function shouldDisplayDeepResearchTool(toolName: string): boolean {
  // Include tool if it's in the known list OR if it's an MCP tool
  return (
    TOOLS_TO_DISPLAY.includes(toolName as DeepResearchToolType) ||
    (typeof toolName === 'string' && isMCPTool(toolName))
  );
}

/**
 * Get the display name for a Deep Research tool.
 * Uses status-based display names: present tense for pending, past tense for completed.
 */
export function getDeepResearchToolDisplayName(toolName: string, status: ToolCallStatus): string {
  if (isMCPTool(toolName)) {
    return status === ToolCallStatus.COMPLETED ? 'Used MCP tool' : 'Using MCP tool';
  }

  const descriptions =
    status === ToolCallStatus.COMPLETED ? TOOL_DESCRIPTIONS_COMPLETED : TOOL_DESCRIPTIONS_IN_PROGRESS;
  const description = descriptions[toolName as DeepResearchToolType];
  if (description) {
    return description;
  }

  // For unknown tools, use a generic description
  return status === ToolCallStatus.COMPLETED ? 'Worked on your request' : 'Working on your request';
}
